import { useState } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { authClient } from "@/lib/auth";
import { env } from "@/lib/env";
import { TurnstileWidget } from "./components/turnstile-widget";

/** Only same-site relative paths are allowed as a post-login destination. */
function safeRedirect(value: string | undefined): string {
  if (value && value.startsWith("/") && !value.startsWith("//")) {
    return value;
  }
  return "/";
}

const GOOGLE_LOGO = (
  <svg viewBox="0 0 48 48" className="size-5" aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

/**
 * Sign-in screen for the public site. A Google account is required to use
 * celebrity mode, watch the public feed, and post clips. Sign-in is protected
 * by a Cloudflare Turnstile widget whose token is forwarded to the backend in
 * the `x-captcha-response` header before any OAuth redirect is issued. The
 * optional `redirect` search param names the path to return to afterwards.
 */
export function LoginPage() {
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const { redirect: redirectParam } = useSearch({ from: "/login" });
  const redirectPath = safeRedirect(redirectParam);

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [securityError, setSecurityError] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const siteKey = env.VITE_TURNSTILE_SITE_KEY;

  const handleSignIn = async () => {
    setError(null);
    if (!siteKey) {
      setError("Sign-in is temporarily unavailable. Please try again later.");
      return;
    }
    if (!turnstileToken || securityError) {
      setError("Complete the security check first.");
      return;
    }
    setSigningIn(true);
    try {
      const res = await authClient.signIn.social({
        provider: "google",
        // Absolute URL: the auth server resolves a relative callback against
        // its own origin (the worker), so the browser would end up on the
        // worker instead of the frontend after the OAuth round-trip.
        callbackURL: `${window.location.origin}${redirectPath}`,
        disableRedirect: true,
        fetchOptions: { headers: { "x-captcha-response": turnstileToken } },
      });
      if (res.error) {
        throw new Error(res.error.message ?? res.error.code ?? "Sign-in failed.");
      }
      if (!res.data?.url) {
        throw new Error("No sign-in URL was returned.");
      }
      window.location.href = res.data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed. Try again.");
      // Re-mount the widget so a fresh Turnstile token is issued.
      setAttempt((n) => n + 1);
      setTurnstileToken(null);
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gradient-to-b from-[#17193b] to-black text-white">
      <div className="mx-auto flex min-h-full max-w-sm flex-col px-4 py-6">
        <header className="mb-8">
          <Link
            to="/"
            className="inline-flex size-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Back to workout"
          >
            <ArrowLeft className="size-5" />
          </Link>
        </header>

        {sessionPending ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="size-5 animate-spin text-white/60" />
          </div>
        ) : session?.session ? (
          <div className="flex flex-1 flex-col justify-center">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center">
              <div className="mx-auto flex size-14 items-center justify-center overflow-hidden rounded-full bg-white/10">
                {session.user.image ? (
                  <img
                    src={session.user.image}
                    alt=""
                    className="size-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span className="text-lg font-semibold">
                    {(session.user.name ?? "?")[0]?.toUpperCase()}
                  </span>
                )}
              </div>
              <h1 className="mt-4 text-xl font-light tracking-tight">Signed in</h1>
              <p className="mt-1 text-sm text-white/60">
                {session.user.name ?? session.user.email} — celebrity mode and the public feed are
                unlocked.
              </p>
              <Link
                to={redirectPath}
                className="mt-6 inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-[#00a2ff] px-6 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(0,162,255,0.35)] transition-colors hover:bg-[#1badff]"
              >
                Continue
              </Link>
            </div>
          </div>
        ) : (
          <div className="flex flex-1 flex-col justify-center">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h1 className="text-2xl font-light tracking-tight">Sign in</h1>
              <p className="mt-1.5 text-sm text-white/55">
                Use your Google account to compete against celebrities and watch the public feed.
              </p>

              <div className="mt-6">
                {siteKey ? (
                  <TurnstileWidget
                    key={attempt}
                    siteKey={siteKey}
                    action="signin"
                    onToken={(token) => {
                      setTurnstileToken(token);
                      setSecurityError(false);
                    }}
                    onExpire={() => {
                      setTurnstileToken(null);
                      setSecurityError(true);
                    }}
                  />
                ) : (
                  <p className="rounded-lg border border-amber-500/30 bg-amber-950/40 px-3 py-2 text-xs text-amber-200">
                    Security check unavailable — sign-in is disabled.
                  </p>
                )}
              </div>

              {error && (
                <p className="mt-3 rounded-lg border border-red-500/30 bg-red-950/50 px-3 py-2 text-xs text-red-200">
                  {error}
                </p>
              )}

              <button
                type="button"
                onClick={() => void handleSignIn()}
                disabled={signingIn}
                className="mt-4 flex h-12 w-full items-center justify-center gap-2.5 rounded-xl bg-white text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-200 disabled:pointer-events-none disabled:opacity-60"
              >
                {signingIn ? <Loader2 className="size-5 animate-spin" /> : GOOGLE_LOGO}
                {signingIn ? "Redirecting…" : "Continue with Google"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
